<template>
    <div>
        <!-- Selector de Modelo -->
        <v-select
            v-model="selectedModel"
            :items="modelOptions"
            label="Select Model"
            prepend-icon="mdi-shape"
            class="mb-4"
            @change="loadTutorial"
        />

        <!-- Campo de URL de YouTube (para todos los modelos) -->
        <v-text-field
            v-model="youtubeUrl"
            label="Youtube URL"
            prepend-icon="mdi-link"
            :loading="saving"
            @change="saveUrl"
            class="mb-4"
        />

        <!-- Campo de carga de archivo (solo para HPL y Aluminum) -->
        <div
            v-if="
                selectedModel === 'HPL PANELS' ||
                    selectedModel === 'ALUMINUM CLADDING'
            "
        >
            <v-card class="mb-4" outlined>
                <v-card-title class="text-subtitle-1">
                    Tutorial Document (PDF)
                </v-card-title>
                <v-card-text>
                    <div v-if="tutorialDocument">
                        <div class="d-flex align-center mb-2">
                            <v-icon color="primary" class="mr-2">
                                mdi-file-pdf-box
                            </v-icon>
                            <span
                                class="file-link"
                                @click="openDocument"
                                :style="{ color: '#2b81d6', cursor: 'pointer' }"
                            >
                                {{
                                    tutorialDocument.name ||
                                        tutorialDocument.fileName
                                }}
                            </span>
                            <v-btn
                                icon
                                small
                                @click="deleteDocument"
                                class="ml-2"
                                :disabled="saving"
                            >
                                <v-icon color="error">mdi-delete</v-icon>
                            </v-btn>
                        </div>
                    </div>
                    <v-file-input
                        v-else
                        v-model="selectedFile"
                        label="Upload PDF Document"
                        accept=".pdf"
                        prepend-icon="mdi-paperclip"
                        :disabled="saving"
                    />
                </v-card-text>
                <v-card-actions v-if="selectedFile && !tutorialDocument">
                    <v-spacer />
                    <v-btn
                        color="primary"
                        @click="uploadDocument"
                        :loading="uploading"
                        :disabled="!selectedFile"
                    >
                        Upload Document
                    </v-btn>
                </v-card-actions>
            </v-card>
        </div>

        <!-- Botón de guardar -->
        <v-btn
            color="primary"
            @click="dialog = true"
            :disabled="!youtubeId"
            :loading="saving"
        >
            SAVE
        </v-btn>

        <!-- Diálogo de preview -->
        <v-dialog v-model="dialog" max-width="600px">
            <v-card>
                <v-card-title>Tutorial - {{ selectedModel }}</v-card-title>
                <v-card-text>
                    <v-img
                        v-if="youtubeId"
                        :src="
                            `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`
                        "
                        max-width="600"
                        class="mb-2"
                    />
                    <v-btn
                        color="primary"
                        :href="`https://www.youtube.com/watch?v=${youtubeId}`"
                        target="_blank"
                        :disabled="!youtubeId"
                        class="mb-2"
                    >
                        Watch on YouTube
                    </v-btn>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="dialog = false">Cerrar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapMutations } from 'vuex'
import API from '@/services/api'
import { saveFile } from '@/services/storage/saveFile.js'
import { openFile } from '@/services/storage/openFile.js'
import { deleteFile } from '@/services/storage/deleteFile.js'
import { v4 as uuidv4 } from 'uuid'

export default {
    name: 'VideoTutorial',
    props: {
        setting: Object,
    },
    data() {
        return {
            selectedModel: 'louvers', // Default para louvers
            modelOptions: [
                { text: 'Louvers', value: 'louvers' },
                { text: 'HPL CLADDING', value: 'HPL PANELS' },
                { text: 'ALUMINUM CLADDING', value: 'ALUMINUM CLADDING' },
            ],
            youtubeUrl: '',
            selectedFile: null,
            tutorialDocument: null, // { fileName: string, name: string }
            dialog: false,
            saving: false,
            uploading: false,
            companyId: JSON.parse(localStorage.getItem('company')),
            folderPath: 'settings/client_portal/tutorials',
        }
    },
    computed: {
        youtubeId() {
            if (!this.youtubeUrl) return null
            const match = this.youtubeUrl.match(
                /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]+)/
            )
            return match ? match[1] : null
        },
    },
    async mounted() {
        // Cargar el tutorial del modelo por defecto
        if (this.setting && this.setting.id) {
            await this.loadTutorial()
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),

        async loadTutorial() {
            if (!this.setting || !this.setting.id) return

            try {
                this.saving = true
                const tutorial = await API.getTutorial({
                    settingId: this.setting.id,
                    model: this.selectedModel,
                })

                if (tutorial) {
                    this.youtubeUrl = tutorial.url || ''
                    this.tutorialDocument = tutorial.fileName
                        ? {
                              fileName: tutorial.fileName, // ID del archivo
                              name: tutorial.name || tutorial.fileName, // Nombre de presentación
                          }
                        : null
                } else {
                    this.youtubeUrl = ''
                    this.tutorialDocument = null
                }
            } catch (e) {
                this.setErrorItems({
                    source: 'VideoTutorial',
                    message: e.message,
                })
            } finally {
                this.saving = false
            }
        },

        async saveUrl() {
            if (!this.setting || !this.setting.id) return
            this.saving = true
            try {
                await API.saveTutorial({
                    settingId: this.setting.id,
                    model: this.selectedModel,
                    url: this.youtubeUrl,
                    fileName: this.tutorialDocument?.fileName || null,
                    name: this.tutorialDocument?.name || null,
                })
            } catch (e) {
                this.setErrorItems({
                    source: 'VideoTutorial',
                    message: e.message,
                })
            } finally {
                this.saving = false
            }
        },

        async uploadDocument() {
            if (!this.selectedFile || !this.setting || !this.setting.id) return

            try {
                this.uploading = true

                // Generar ID único para el nombre del archivo en Storage
                const ext = this.selectedFile.name
                    .slice(this.selectedFile.name.lastIndexOf('.'))
                    .toLowerCase()
                const fileId = `${uuidv4()}${ext}` // ID único para Storage

                // Generar nombre de presentación amigable basado en el modelo
                const displayName = this.selectedModel === 'HPL PANELS' 
                    ? 'HPL Cladding Tutorial'
                    : this.selectedModel === 'ALUMINUM CLADDING'
                    ? 'Aluminum Cladding Tutorial'
                    : 'Tutorial'

                // Guardar archivo en Firebase Storage con el ID como nombre
                await saveFile(
                    this.selectedFile,
                    `${this.companyId}/${this.folderPath}`,
                    fileId // Guardar con ID
                )

                // Guardar referencia en la base de datos con fileName (ID) y name (presentación)
                await API.saveTutorial({
                    settingId: this.setting.id,
                    model: this.selectedModel,
                    url: this.youtubeUrl,
                    fileName: fileId, // ID del archivo en Storage
                    name: displayName, // Nombre de presentación
                })

                // Actualizar estado local
                this.tutorialDocument = {
                    fileName: fileId, // ID para acceder al archivo
                    name: displayName, // Nombre para mostrar
                }
                this.selectedFile = null
            } catch (e) {
                this.setErrorItems({
                    source: 'VideoTutorial',
                    message: e.message,
                })
            } finally {
                this.uploading = false
            }
        },

        async openDocument() {
            if (!this.tutorialDocument || !this.tutorialDocument.fileName)
                return

            try {
                this.saving = true
                await openFile(
                    this.tutorialDocument.fileName,
                    `${this.companyId}/${this.folderPath}`
                )
            } catch (e) {
                this.setErrorItems({
                    source: 'VideoTutorial',
                    message: e.message,
                })
            } finally {
                this.saving = false
            }
        },

        async deleteDocument() {
            if (!this.tutorialDocument || !this.tutorialDocument.fileName)
                return

            try {
                this.saving = true

                // Eliminar archivo de Firebase Storage
                await deleteFile(
                    this.tutorialDocument.fileName,
                    `${this.companyId}/${this.folderPath}`
                )

                // Actualizar en base de datos
                await API.saveTutorial({
                    settingId: this.setting.id,
                    model: this.selectedModel,
                    url: this.youtubeUrl,
                    fileName: null,
                    name: null,
                })

                // Limpiar estado local
                this.tutorialDocument = null
            } catch (e) {
                this.setErrorItems({
                    source: 'VideoTutorial',
                    message: e.message,
                })
            } finally {
                this.saving = false
            }
        },
    },
}
</script>
